import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MDEditor from '@uiw/react-md-editor';
import { supportAgentApi, InstructionsResult } from './support-agent-api';
import { VersionHistoryDrawer, VersionHistoryEntry } from './version-history-drawer';

const SNIPPET_LENGTH = 160;

/**
 * Instructions panel (design doc §6.7 panel 2). A single editable document
 * (`agentConfig` keyed `instructions`) layered into the draft prompt — the
 * worker re-reads it (cached ~1 min), so saves here go live without a
 * deploy. Unlike the KB doc versions, `InstructionsVersionSnapshot` DOES
 * include the full text, so restoring a prior version into the editor is
 * fully supported (still requires clicking Save to make it live).
 */
export const InstructionsPanel: React.FC = () => {
  const [current, setCurrent] = useState<InstructionsResult | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supportAgentApi.getInstructions();
      setCurrent(res);
      setText(res.text);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await supportAgentApi.updateInstructions(text);
      setCurrent(res);
      setNotice('Saved — the worker picks this up within its ~1 minute cache window.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const versionEntries: VersionHistoryEntry[] = (current?.versions ?? []).map((v, idx) => ({
    key: `${v.updatedAt}-${idx}`,
    updatedBy: v.updatedBy,
    updatedAt: v.updatedAt,
    summary: (
      <>
        <br />
        {v.text.slice(0, SNIPPET_LENGTH)}
        {v.text.length > SNIPPET_LENGTH ? '…' : ''}
      </>
    ),
    onRestore: () => {
      setText(v.text);
      setVersionDrawerOpen(false);
      setNotice('Loaded a prior version into the editor — click Save to make it live.');
    },
    // Unlike the KB drawer's onView (a network round trip — see
    // knowledge-base-panel.tsx / getKbDocVersion), instructions version
    // snapshots already carry the full text client-side (see this file's
    // header comment), so this just resolves it locally. No status/tags —
    // the instructions doc has neither.
    onView: async () => ({
      title: 'Instructions',
      body: v.text,
      updatedBy: v.updatedBy,
      updatedAt: v.updatedAt,
    }),
  }));

  const isDirty = current !== null && text !== current.text;

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Saving here changes the live bot immediately — the worker re-reads instructions on every job (cached ~1
        minute). There is one instructions doc, shared by dev, QA, and prod.
      </Alert>
      <Alert severity="info" sx={{ mb: 2 }}>
        This is additive guidance only (tone, standing rules, per-topic notes). Safety-critical behavior — the
        urgency screen, private-note-only writes, and the injection posture — lives in code and only changes via a
        PR, not through this panel.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {current?.updatedAt
            ? `Last saved by ${current.updatedBy} on ${new Date(current.updatedAt).toLocaleString()}`
            : 'No instructions saved yet.'}
        </Typography>
        <Button startIcon={<HistoryIcon />} size="small" onClick={() => setVersionDrawerOpen(true)}>
          Change history ({current?.versions.length ?? 0})
        </Button>
      </Stack>

      <MDEditor value={text} onChange={(v) => setText(v ?? '')} height={420} preview="live" />

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!isDirty || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {isDirty && (
          <Button variant="text" disabled={saving} onClick={() => setText(current?.text ?? '')}>
            Discard changes
          </Button>
        )}
      </Stack>

      <VersionHistoryDrawer
        open={versionDrawerOpen}
        onClose={() => setVersionDrawerOpen(false)}
        title="Instructions change history"
        entries={versionEntries}
        emptyMessage="No prior versions yet — this is the first save."
      />
    </Box>
  );
};
